/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
import * as acorn from 'acorn';

import { HEAL_PW_FUNCTIONS, ParsedFunctionCall, VariablePosition } from '../types/LiveAgentEditorTypes.js';

const DEBUG_PARSER = false;

// eslint-disable-next-line no-shadow
enum CodeSegmentType {
    HealFunctionCall = 'HealFunctionCall',
    Comment = 'Comment',
    ExecPlaywrightSegment = 'ExecPlaywrightSegment',
    VariablesSegment = 'VariablesSegment',
}

interface CodeSegment {
    type: CodeSegmentType;
    methodName?: string;
    args?: { value: any; type: string }[];
    content: string;
    start: number;
    end: number;
    variableName?: string;
}

interface BlockFunctionInfo {
    isBlockFunction: boolean;
    functionBody?: any[];
    blockStatement?: any;
}

/**
 * A parser that analyzes TypeScript code to extract function calls and execPlaywright segments.
 * It uses acorn to parse and traverse the AST, identifying Heal function calls and
 * segments of code that should be executed as Playwright scripts.
 */
export class AstParser {
    /**
     * Retrieves the literal value of a node.
     * @param node - The AST node to get the literal value from
     * @param code - The original source code string
     * @returns The literal value of the node
     */
    private static getLiteralValue(node: any, code: string): { value: any; type: string } {
        // Handle different types of literals
        switch (node.type) {
            case 'Identifier':
                return { value: node.name, type: 'Identifier' };
            case 'Literal':
                return { value: node.value, type: 'Literal' };
            case 'ObjectExpression':
                const obj: any = {};
                for (const prop of node.properties) {
                    const key =
                        prop.key.type === 'Identifier' ? prop.key.name : this.getLiteralValue(prop.key, code).value;
                    obj[key] = this.getLiteralValue(prop.value, code).value;
                }
                return { value: obj, type: 'ObjectExpression' };
            case 'ArrayExpression':
                return {
                    value: node.elements.map((element: any) => this.getLiteralValue(element, code)),
                    type: 'ArrayExpression',
                };
            case 'CallExpression':
                // Return the raw code string of the function call
                return {
                    value: node.raw || code.substring(node.start, node.end),
                    type: 'CallExpression',
                };
            case 'TemplateLiteral':
                // Handle template literals by combining the quasis and expressions
                let templateString = '';
                for (let i = 0; i < node.quasis.length; ++i) {
                    templateString += node.quasis[i].value.raw;
                    if (i < node.expressions.length) {
                        const exprValue = this.getLiteralValue(node.expressions[i], code);
                        const formattedValue =
                            typeof exprValue.value === 'string' ? exprValue.value : JSON.stringify(exprValue.value);
                        templateString += `\${${formattedValue}}`;
                    }
                }
                return { value: templateString, type: 'TemplateLiteral' };
            default:
                // For unsupported nodes, return a string representation
                return { value: node.raw || JSON.stringify(node), type: 'Unsupported' };
        }
    }

    /**
     * Parses the given TypeScript code and extracts function calls and execPlaywright segments.
     * @param code - The TypeScript code to parse
     * @returns An array of parsed function calls and execPlaywright segments
     */
    parseCode(code: string): ParsedFunctionCall[] {
        // Parse the code using acorn
        let ast;
        try {
            ast = acorn.parse(code, {
                ecmaVersion: 'latest',
                sourceType: 'module',
                allowAwaitOutsideFunction: true,
                locations: true,
                ranges: true,
            });
        } catch (err) {
            if (DEBUG_PARSER) {
                // eslint-disable-next-line no-console
                console.error('Acorn parsing error:', err);
            }
            return [];
        }

        const lines = code.split('\n');
        const lineCount = lines.length;

        // Check if this is a Heal.blocks function definition and get its body nodes
        const blockFunctionInfo = this.detectBlockFunction(ast, code);

        // Extract code segments from the AST root nodes
        const codeSegments: CodeSegment[] = [];

        // Extract comments (acorn doesn't include comments in the AST by default)
        const comments = this.extractComments(code, blockFunctionInfo);
        codeSegments.push(...comments);

        // Process each node at the root level (direct children of Program)
        if (ast.body && Array.isArray(ast.body)) {
            if (DEBUG_PARSER) {
                // Log children types
                for (const child of ast.body) {
                    // eslint-disable-next-line no-console
                    console.log(`Child type: ${child.type}`);
                }
            }

            // If this is a block function, process its body content instead of root nodes
            if (blockFunctionInfo.isBlockFunction && blockFunctionInfo.functionBody) {
                // Process the function body nodes directly
                for (const node of blockFunctionInfo.functionBody) {
                    codeSegments.push(this.processRootNode(node, code));
                }
            } else {
                // Process each root-level node normally
                for (const node of ast.body) {
                    codeSegments.push(this.processRootNode(node, code));
                }
            }
        }

        // Sort code segments by start position
        codeSegments.sort((a, b) => a.start - b.start);

        if (DEBUG_PARSER) {
            // eslint-disable-next-line no-console
            console.log('Code segments:', codeSegments);
        }

        // Group segments according to the rules
        const groupedSegments = this.mergeSegments(codeSegments);

        if (DEBUG_PARSER) {
            // eslint-disable-next-line no-console
            console.log('Grouped segments:', groupedSegments);
        }

        return this.createFunctionCalls(groupedSegments, lines, lineCount, code, blockFunctionInfo);
    }

    /**
     * Detects if the AST represents a Heal.blocks function definition and extracts its body.
     * @param ast - The parsed AST
     * @param code - The original source code
     * @returns Information about the block function
     */
    private detectBlockFunction(ast: any, code: string): BlockFunctionInfo {
        // Check if we have exactly one statement that's an assignment to Heal.blocks.something
        if (!ast.body || ast.body.length !== 1) {
            return { isBlockFunction: false };
        }

        const statement = ast.body[0];

        // Check if it's an expression statement
        if (statement.type !== 'ExpressionStatement') {
            return { isBlockFunction: false };
        }

        const expression = statement.expression;

        // Check if it's an assignment expression
        if (expression.type !== 'AssignmentExpression') {
            return { isBlockFunction: false };
        }

        // Check if the left side is Heal.blocks.something
        const left = expression.left;
        if (
            left.type !== 'MemberExpression' ||
            left.object.type !== 'MemberExpression' ||
            left.object.object.type !== 'Identifier' ||
            left.object.object.name !== 'Heal' ||
            left.object.property.type !== 'Identifier' ||
            left.object.property.name !== 'blocks' ||
            left.property.type !== 'Identifier'
        ) {
            return { isBlockFunction: false };
        }

        // Check if the right side is an async arrow function
        const right = expression.right;
        if (right.type !== 'ArrowFunctionExpression' || !right.async) {
            return { isBlockFunction: false };
        }

        // Check if the function body is a block statement
        if (right.body.type !== 'BlockStatement') {
            return { isBlockFunction: false };
        }

        // Return the function body statements and the block statement itself
        return {
            isBlockFunction: true,
            functionBody: right.body.body,
            blockStatement: right.body,
        };
    }

    /**
     * Extracts block function information (name and parameters) from code.
     * @param code - The source code to analyze
     * @returns Object with function name, arguments, return info, and list of inner blocks,
     * or null if not a block function
     */
    extractBlockFunctionInfo(
        code: string,
    ): { name: string; args: string[]; hasReturnValue: boolean; innerBlocks: string[] } | null {
        try {
            // Parse the code using acorn
            let ast;
            try {
                ast = acorn.parse(code, {
                    ecmaVersion: 'latest',
                    sourceType: 'module',
                    allowAwaitOutsideFunction: true,
                    locations: true,
                    ranges: true,
                });
            } catch (err) {
                return null;
            }

            // Check if we have exactly one statement that's an assignment to Heal.blocks.something
            if (!ast.body || ast.body.length !== 1) {
                return null;
            }

            const statement = ast.body[0];

            // Check if it's an expression statement
            if (statement.type !== 'ExpressionStatement') {
                return null;
            }

            const expression = statement.expression;

            // Check if it's an assignment expression
            if (expression.type !== 'AssignmentExpression') {
                return null;
            }

            // Check if the left side is Heal.blocks.something
            const left = expression.left;
            if (
                left.type !== 'MemberExpression' ||
                left.object.type !== 'MemberExpression' ||
                left.object.object.type !== 'Identifier' ||
                left.object.object.name !== 'Heal' ||
                left.object.property.type !== 'Identifier' ||
                left.object.property.name !== 'blocks' ||
                left.property.type !== 'Identifier'
            ) {
                return null;
            }

            // Extract the function name
            const functionName = left.property.name;

            // Check if the right side is an async arrow function
            const right = expression.right;
            if (right.type !== 'ArrowFunctionExpression' || !right.async) {
                return null;
            }

            // Extract the function parameters
            const args = right.params.map((param: any) => {
                if (param.type === 'Identifier') {
                    return param.name;
                }
                // Handle other parameter types if needed (destructuring, etc.)
                return 'unknown';
            });

            // Check if the function has a return value
            const hasReturnValue = this.checkFunctionHasReturnValue(right.body);

            // Extract inner block function calls
            const innerBlocks = this.extractInnerBlockCalls(right.body);

            return { name: functionName, args, hasReturnValue, innerBlocks };
        } catch (error) {
            return null;
        }
    }

    /**
     * Recursively extracts all Heal.blocks.* function calls from a function body.
     * @param node - The AST node to traverse (function body)
     * @returns Array of inner block function names
     */
    private extractInnerBlockCalls(node: any): string[] {
        const innerBlocks: string[] = [];

        if (!node) return innerBlocks;

        // Visitor function to traverse the AST
        const visitNode = (currentNode: any) => {
            if (!currentNode) return;

            // Check for call expressions
            if (currentNode.type === 'CallExpression') {
                const callee = currentNode.callee;

                // Check if it's a Heal.blocks.* call
                if (
                    callee.type === 'MemberExpression' &&
                    callee.object.type === 'MemberExpression' &&
                    callee.object.object.type === 'Identifier' &&
                    callee.object.object.name === 'Heal' &&
                    callee.object.property.type === 'Identifier' &&
                    callee.object.property.name === 'blocks' &&
                    callee.property.type === 'Identifier'
                ) {
                    innerBlocks.push(callee.property.name);
                }
            }

            // Recursively visit child nodes
            for (const key in currentNode) {
                if (currentNode[key] && typeof currentNode[key] === 'object') {
                    if (Array.isArray(currentNode[key])) {
                        for (const child of currentNode[key]) {
                            if (child && typeof child === 'object') {
                                visitNode(child);
                            }
                        }
                    } else if (currentNode[key].type) {
                        visitNode(currentNode[key]);
                    }
                }
            }
        };

        // Start traversal at the function body
        visitNode(node);

        // Remove duplicates and return
        return [...new Set(innerBlocks)];
    }

    /**
     * Recursively checks if a function body contains a return statement with a value other than undefined.
     * @param node - The AST node to check (function body)
     * @returns True if the function has a return statement with a value
     */
    private checkFunctionHasReturnValue(node: any): boolean {
        if (!node) return false;

        // If it's a block statement, check all statements inside
        if (node.type === 'BlockStatement') {
            for (const statement of node.body || []) {
                if (this.checkFunctionHasReturnValue(statement)) {
                    return true;
                }
            }
            return false;
        }

        // If it's a return statement, check if it has an argument
        if (node.type === 'ReturnStatement') {
            // Return true if there's an argument (return value)
            // Return false for bare 'return;' or 'return undefined;'
            if (!node.argument) {
                return false; // bare return;
            }

            // Check for explicit undefined
            if (node.argument.type === 'Identifier' && node.argument.name === 'undefined') {
                return false; // return undefined;
            }

            return true; // Has a return value
        }

        // For other statement types, recursively check their children
        if (node.type === 'IfStatement') {
            return (
                this.checkFunctionHasReturnValue(node.consequent) || this.checkFunctionHasReturnValue(node.alternate)
            );
        }

        if (node.type === 'TryStatement') {
            return (
                this.checkFunctionHasReturnValue(node.block) ||
                this.checkFunctionHasReturnValue(node.handler?.body) ||
                this.checkFunctionHasReturnValue(node.finalizer)
            );
        }

        if (node.type === 'SwitchStatement') {
            for (const case_ of node.cases || []) {
                for (const statement of case_.consequent || []) {
                    if (this.checkFunctionHasReturnValue(statement)) {
                        return true;
                    }
                }
            }
            return false;
        }

        if (
            node.type === 'WhileStatement' ||
            node.type === 'DoWhileStatement' ||
            node.type === 'ForStatement' ||
            node.type === 'ForInStatement' ||
            node.type === 'ForOfStatement'
        ) {
            return this.checkFunctionHasReturnValue(node.body);
        }

        // For other node types, recursively check all child nodes
        for (const key in node) {
            const child = node[key];
            if (child && typeof child === 'object') {
                if (Array.isArray(child)) {
                    for (const item of child) {
                        if (item && typeof item === 'object' && this.checkFunctionHasReturnValue(item)) {
                            return true;
                        }
                    }
                } else if (child.type && this.checkFunctionHasReturnValue(child)) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * Process a single root-level node in the AST.
     * @param node - The AST node to process
     * @param code - The original source code
     * @returns A CodeSegment representing the processed node
     */
    private processRootNode(node: any, code: string): CodeSegment {
        // Handle variable declarations that assign the result of Heal functions
        if (node.type === 'VariableDeclaration' && node.declarations && node.declarations.length === 1) {
            const declaration = node.declarations[0];

            if (DEBUG_PARSER) {
                // eslint-disable-next-line no-console
                console.log(`Found variable declaration: ${node.kind} ${declaration.id?.name || 'unknown'}`);
            }

            // Check if it's initializing with an await expression
            if (declaration.init && declaration.init.type === 'AwaitExpression') {
                const awaitExpr = declaration.init;

                // Check if it's awaiting a Heal function call
                if (awaitExpr.argument && awaitExpr.argument.type === 'CallExpression') {
                    const callExpr = awaitExpr.argument;

                    if (DEBUG_PARSER && callExpr.callee?.type === 'MemberExpression') {
                        const memberExpr = callExpr.callee;
                        // eslint-disable-next-line no-console
                        console.log(
                            `Found await call: ${memberExpr.object?.name || 'unknown'}.${
                                memberExpr.property?.name || 'unknown'
                            }`,
                        );
                    }

                    // Process the call expression inside the await, passing the variable name
                    if (declaration.id && declaration.id.type === 'Identifier') {
                        const variableName = declaration.id.name;

                        if (DEBUG_PARSER) {
                            // eslint-disable-next-line no-console
                            console.log(`Processing variable assignment: ${variableName}`);
                        }

                        return this.processCallExpressionAsVariableAssignment(callExpr, node, code, variableName);
                    }
                }
            }
        }

        // Handle expression statements (which might contain Heal function calls)
        if (node.type === 'ExpressionStatement') {
            // Check if it's an await expression
            if (node.expression.type === 'AwaitExpression') {
                const awaitExpr = node.expression;
                if (awaitExpr.argument.type === 'CallExpression') {
                    // Process the call expression inside the await
                    const callExpr = awaitExpr.argument;
                    return this.processCallExpressionAsSegment(callExpr, node, code);
                }
            } else if (node.expression.type === 'CallExpression') {
                // Direct call expression (not in await)
                return this.processCallExpressionAsSegment(node.expression, node, code);
            }
        }

        // For any other node type or non-Heal function calls,
        // treat as ExecPlaywrightSegment
        return {
            type: CodeSegmentType.ExecPlaywrightSegment,
            content: code.substring(node.start, node.end),
            start: node.start,
            end: node.end,
        };
    }

    /**
     * Process a call expression and return the appropriate CodeSegment.
     * @param callExpr - The call expression to process
     * @param parentNode - The parent node containing the call expression
     * @param code - The original source code
     * @returns A CodeSegment representing the processed call expression
     */
    private processCallExpressionAsSegment(callExpr: any, parentNode: any, code: string): CodeSegment {
        // Check if it's a member expression (e.g., Heal.methodName())
        if (callExpr.callee.type === 'MemberExpression') {
            const memberExpr = callExpr.callee;

            // Check if it's a simple Heal function call (Heal.methodName)
            if (
                memberExpr.object.type === 'Identifier' &&
                memberExpr.object.name === 'Heal' &&
                memberExpr.property.type === 'Identifier' &&
                !HEAL_PW_FUNCTIONS.includes(memberExpr.property.name)
            ) {
                return {
                    type: CodeSegmentType.HealFunctionCall,
                    methodName: memberExpr.property.name,
                    args: callExpr.arguments.map((arg: any) => AstParser.getLiteralValue(arg, code)),
                    content: code.substring(parentNode.start, parentNode.end),
                    start: parentNode.start,
                    end: parentNode.end,
                };
            }

            // Check if it's a nested Heal function call (Heal.blocks.blockName)
            if (
                memberExpr.object.type === 'MemberExpression' &&
                memberExpr.object.object.type === 'Identifier' &&
                memberExpr.object.object.name === 'Heal' &&
                memberExpr.object.property.type === 'Identifier' &&
                memberExpr.object.property.name === 'blocks' &&
                memberExpr.property.type === 'Identifier'
            ) {
                return {
                    type: CodeSegmentType.HealFunctionCall,
                    methodName: `blocks.${memberExpr.property.name}`,
                    args: callExpr.arguments.map((arg: any) => AstParser.getLiteralValue(arg, code)),
                    content: code.substring(parentNode.start, parentNode.end),
                    start: parentNode.start,
                    end: parentNode.end,
                };
            }
        }

        // If not a Heal function call, treat as ExecPlaywrightSegment
        return {
            type: CodeSegmentType.ExecPlaywrightSegment,
            content: code.substring(parentNode.start, parentNode.end),
            start: parentNode.start,
            end: parentNode.end,
        };
    }

    /**
     * Process a call expression in a variable assignment and return the appropriate CodeSegment.
     * @param callExpr - The call expression to process
     * @param parentNode - The parent node containing the call expression
     * @param code - The original source code
     * @param variableName - The name of the variable being assigned
     * @returns A CodeSegment representing the processed call expression with variable assignment
     */
    private processCallExpressionAsVariableAssignment(
        callExpr: any,
        parentNode: any,
        code: string,
        variableName: string,
    ): CodeSegment {
        // Check if it's a member expression (e.g., Heal.methodName())
        if (callExpr.callee.type === 'MemberExpression') {
            const memberExpr = callExpr.callee;

            // Check if it's a simple Heal function call that returns a value
            if (
                memberExpr.object.type === 'Identifier' &&
                memberExpr.object.name === 'Heal' &&
                memberExpr.property.type === 'Identifier' &&
                ['extractSelector', 'aiExtractInfo', 'readSMS', 'emailSignup'].includes(memberExpr.property.name)
            ) {
                // Extract the arguments for the function call
                const argValues = callExpr.arguments.map((arg: any) => AstParser.getLiteralValue(arg, code));

                if (DEBUG_PARSER) {
                    // eslint-disable-next-line no-console
                    console.log(`Variable assignment for ${memberExpr.property.name}:`);
                    // eslint-disable-next-line no-console
                    console.log(`  - Variable: ${variableName}`);
                    // eslint-disable-next-line no-console
                    console.log(`  - Arguments: ${JSON.stringify(argValues)}`);
                }

                // These are the functions that now return values directly
                return {
                    type: CodeSegmentType.HealFunctionCall,
                    methodName: memberExpr.property.name,
                    // Use just the original arguments, not including the variable name
                    args: argValues,
                    content: code.substring(parentNode.start, parentNode.end),
                    start: parentNode.start,
                    end: parentNode.end,
                    variableName, // Store variableName in dedicated property
                };
            }

            // Check if it's a nested Heal function call (Heal.blocks.blockName) that might return a value
            if (
                memberExpr.object.type === 'MemberExpression' &&
                memberExpr.object.object.type === 'Identifier' &&
                memberExpr.object.object.name === 'Heal' &&
                memberExpr.object.property.type === 'Identifier' &&
                memberExpr.object.property.name === 'blocks' &&
                memberExpr.property.type === 'Identifier'
            ) {
                // Extract the arguments for the function call
                const argValues = callExpr.arguments.map((arg: any) => AstParser.getLiteralValue(arg, code));

                if (DEBUG_PARSER) {
                    // eslint-disable-next-line no-console
                    console.log(`Variable assignment for blocks.${memberExpr.property.name}:`);
                    // eslint-disable-next-line no-console
                    console.log(`  - Variable: ${variableName}`);
                    // eslint-disable-next-line no-console
                    console.log(`  - Arguments: ${JSON.stringify(argValues)}`);
                }

                return {
                    type: CodeSegmentType.HealFunctionCall,
                    methodName: `blocks.${memberExpr.property.name}`,
                    args: argValues,
                    content: code.substring(parentNode.start, parentNode.end),
                    start: parentNode.start,
                    end: parentNode.end,
                    variableName, // Store variableName in dedicated property
                };
            }
        }

        // If not a Heal function call we're interested in, treat as ExecPlaywrightSegment
        return {
            type: CodeSegmentType.ExecPlaywrightSegment,
            content: code.substring(parentNode.start, parentNode.end),
            start: parentNode.start,
            end: parentNode.end,
        };
    }

    /**
     * Extract comments from code as CodeSegment objects.
     * @param code - The source code string
     * @param blockFunctionInfo - Information about whether this is a block function
     * @returns An array of comment code segments
     */
    private extractComments(code: string, blockFunctionInfo: BlockFunctionInfo): CodeSegment[] {
        const commentSegments: CodeSegment[] = [];

        // Parse the code using acorn with onComment option
        const comments: { type: string; value: string; start: number; end: number }[] = [];

        acorn.parse(code, {
            ecmaVersion: 'latest',
            sourceType: 'module',
            allowAwaitOutsideFunction: true,
            locations: true,
            ranges: true,
            onComment: (isBlock, text, start, end) => {
                comments.push({
                    type: isBlock ? 'Block' : 'Line',
                    value: isBlock ? `/*${text}*/` : `//${text}`,
                    start,
                    end,
                });
            },
        });

        // Filter comments to find root-level comments
        // Root-level comments are those that aren't nested within code blocks
        const rootComments = comments.filter((comment) => {
            // For block functions, comments inside the function body should be considered "root-level"
            if (blockFunctionInfo.isBlockFunction && blockFunctionInfo.blockStatement) {
                // For BlockStatement, start is position of '{' and end is position after '}'
                // We need to find the position after '{' and before '}'
                const blockStart = blockFunctionInfo.blockStatement.start;
                const blockEnd = blockFunctionInfo.blockStatement.end;

                // Find position after the opening brace
                let contentStart = blockStart;
                while (contentStart < blockEnd && code[contentStart] !== '{') {
                    contentStart++;
                }
                contentStart++; // Move past the '{'

                // Find position before the closing brace
                let contentEnd = blockEnd;
                while (contentEnd > blockStart && code[contentEnd - 1] !== '}') {
                    contentEnd--;
                }
                contentEnd--; // Move before the '}'

                // Include comments that are within the actual block content
                return comment.start >= contentStart && comment.end <= contentEnd;
            }

            // Check if this comment is inside a code block by looking at the code before it
            const codeBeforeComment = code.substring(0, comment.start);

            // Check for unbalanced braces/parentheses
            let braceCount = 0;
            let parenCount = 0;

            for (let i = 0; i < codeBeforeComment.length; ++i) {
                const char = codeBeforeComment[i];
                if (char === '{') braceCount++;
                else if (char === '}') braceCount--;
                else if (char === '(') parenCount++;
                else if (char === ')') parenCount--;
            }

            // If braces and parentheses are balanced, this is likely a root-level comment
            // We need to add special handling for STEP_SEPARATOR
            return braceCount === 0 && parenCount === 0;
        });

        // Convert filtered comments to CodeSegment objects
        for (const comment of rootComments) {
            commentSegments.push({
                type: CodeSegmentType.Comment,
                content: comment.value,
                start: comment.start,
                end: comment.end,
            });
        }

        // Sort comments by their start position
        commentSegments.sort((a, b) => a.start - b.start);

        return commentSegments;
    }

    /**
     * Groups code segments according to the following rules:
     * - Iterate through segments from start to end
     * - If a segment is HealFunctionCall, group it with comments before it until
     *   another HealFunctionCall or ExecPlaywrightSegment
     * - If a segment is ExecPlaywrightSegment, group it with ExecPlaywrightSegment or
     *   Comment segments before it until a HealFunctionCall
     * - If first nodes are comments, group them with the first HealFunctionCall
     *   or ExecPlaywrightSegment
     * - If a comment contains the PLAYWRIGHT_STEP_SEPARATOR, split the Playwright segments
     * @param segments - The array of code segments to group
     * @returns An array of grouped segments
     */
    private mergeSegments(segments: CodeSegment[]): CodeSegment[][] {
        if (segments.length === 0) return [];

        const groupedSegments: CodeSegment[][] = [];
        let currentGroup: CodeSegment[] = [];
        let pendingComments: CodeSegment[] = [];

        if (DEBUG_PARSER) {
            // eslint-disable-next-line no-console
            console.log('=== MERGE SEGMENTS DEBUG ===');
            // eslint-disable-next-line no-console
            console.log(
                'Input segments:',
                segments.map((s) => `${s.type}: "${s.content.substring(0, 50)}..."`),
            );
        }

        // Process segments from start to end
        for (let i = 0; i < segments.length; ++i) {
            const segment = segments[i];

            if (DEBUG_PARSER) {
                // eslint-disable-next-line no-console
                console.log(`Processing segment ${i}: ${segment.type} - "${segment.content.substring(0, 50)}..."`);
            }

            // Handle comments
            if (segment.type === CodeSegmentType.Comment) {
                // Check if this comment is a Playwright separator
                if (segment.content.includes('STEP_SEPARATOR')) {
                    // If we have a current group with a Playwright segment, complete it
                    if (currentGroup.length > 0) {
                        groupedSegments.push(currentGroup);
                        currentGroup = [];
                        // Don't add the separator comment to pending comments
                        continue;
                    }
                }

                pendingComments.push(segment);
                continue;
            }

            // Handle HealFunctionCall
            if (segment.type === CodeSegmentType.HealFunctionCall) {
                // If we have a current group, save it
                if (currentGroup.length > 0) {
                    groupedSegments.push(currentGroup);
                    currentGroup = [];
                }

                // Create a new group with pending comments and the HealFunctionCall
                const newGroup = [...pendingComments, segment];
                groupedSegments.push(newGroup);
                pendingComments = [];
                continue;
            }

            // Handle ExecPlaywrightSegment
            if (segment.type === CodeSegmentType.ExecPlaywrightSegment) {
                const firstNonCommentSegment = currentGroup.find(
                    (b) =>
                        b.type === CodeSegmentType.HealFunctionCall || b.type === CodeSegmentType.ExecPlaywrightSegment,
                );
                // If we have a current group and it's not an ExecPlaywrightSegment group,
                // save it and start a new one
                if (firstNonCommentSegment && firstNonCommentSegment?.type !== CodeSegmentType.ExecPlaywrightSegment) {
                    groupedSegments.push(currentGroup);
                    currentGroup = [];
                }

                // Add pending comments to the current group
                if (pendingComments.length > 0) {
                    currentGroup.push(...pendingComments);
                    pendingComments = [];
                }

                // Add the ExecPlaywrightSegment to the current group
                currentGroup.push(segment);
            }
        }

        // Add any remaining current group
        if (currentGroup.length > 0) {
            groupedSegments.push(currentGroup);
        }

        // If there are any remaining comments at the end,
        // add them to the last group (they should be trailing comments for the last function)
        if (pendingComments.length > 0 && groupedSegments.length > 0) {
            groupedSegments[groupedSegments.length - 1].push(...pendingComments);
        } else if (pendingComments.length > 0) {
            // If we have pending comments but no groups, create a group for them
            // This shouldn't normally happen in block functions with actual function calls
            groupedSegments.push(pendingComments);
        }

        if (DEBUG_PARSER) {
            // eslint-disable-next-line no-console
            console.log('=== FINAL GROUPED SEGMENTS ===');
            groupedSegments.forEach((group, i) => {
                // eslint-disable-next-line no-console
                console.log(
                    `Group ${i}:`,
                    group.map((s) => `${s.type}: "${s.content.substring(0, 30)}..."`),
                );
            });
        }

        return groupedSegments;
    }

    /**
     * Transforms grouped code segments into ParsedFunctionCall objects.
     * This method processes each group of segments to create function calls with proper
     * positioning information for both the function body and decorations.
     *
     * @param groupedSegments - Array of grouped code segments that will be transformed
     * @param lines - Array of text lines from the source file
     * @param lineCount - Total number of lines in the source file
     * @param code - The original source code
     * @param blockFunctionInfo - Information about whether this is a block function
     * @returns Array of ParsedFunctionCall objects representing the parsed functions
     */
    private createFunctionCalls(
        groupedSegments: CodeSegment[][],
        lines: string[],
        lineCount: number,
        code: string,
        blockFunctionInfo: BlockFunctionInfo,
    ): ParsedFunctionCall[] {
        const functionCalls: ParsedFunctionCall[] = [];

        try {
            // Process grouped segments to create function calls
            for (let i = 0; i < groupedSegments.length; ++i) {
                const group = groupedSegments[i];
                if (group.length === 0) continue;

                if (DEBUG_PARSER) {
                    // eslint-disable-next-line no-console
                    console.log(
                        `Creating function call for group ${i}:`,
                        group.map((s) => `${s.type}: "${s.content.substring(0, 30)}..."`),
                    );
                }

                // Find the first and last non-comment segments in the group
                const firstNonCommentSegment = group.find(
                    (segment) =>
                        segment.type === CodeSegmentType.HealFunctionCall ||
                        segment.type === CodeSegmentType.ExecPlaywrightSegment,
                );
                const lastNonCommentSegment = [...group]
                    .reverse()
                    .find(
                        (segment) =>
                            segment.type === CodeSegmentType.HealFunctionCall ||
                            segment.type === CodeSegmentType.ExecPlaywrightSegment,
                    );

                if (!firstNonCommentSegment || !lastNonCommentSegment) continue;

                // Calculate positions
                const functionPosition = this.getPositionInfo(
                    firstNonCommentSegment.start,
                    lastNonCommentSegment.end,
                    code,
                );

                // Calculate decorations position
                const firstSegment = group[0];
                const nextGroup = groupedSegments[i + 1];

                if (DEBUG_PARSER) {
                    // eslint-disable-next-line no-console
                    console.log(
                        `Group ${i} firstSegment:`,
                        firstSegment.type,
                        `"${firstSegment.content.substring(0, 30)}..."`,
                    );
                    // eslint-disable-next-line no-console
                    console.log(
                        `Group ${i} firstNonCommentSegment:`,
                        firstNonCommentSegment?.type,
                        `"${firstNonCommentSegment?.content.substring(0, 30)}..."`,
                    );
                }

                // For block functions, don't include the function declaration in decorations
                let decorationStart: number;
                if (i === 0) {
                    if (blockFunctionInfo.isBlockFunction) {
                        // For block functions, start from the first segment in the group (could be a comment)
                        // If no segments in the group, fall back to first function body statement
                        if (group.length > 0) {
                            decorationStart = firstSegment.start;
                        } else if (blockFunctionInfo.functionBody && blockFunctionInfo.functionBody.length > 0) {
                            decorationStart = blockFunctionInfo.functionBody[0].start;
                        } else {
                            decorationStart = 0;
                        }
                    } else {
                        decorationStart = 0;
                    }
                } else {
                    decorationStart = firstSegment.start;
                }

                let decorationEnd: number;
                if (nextGroup) {
                    // Check if there's whitespace at the beginning of the next group
                    const nextGroupStart = nextGroup[0].start;
                    const nextGroupLineNumber = this.getLineNumber(nextGroupStart, code);
                    const nextGroupLineStart = this.getLineStartOffset(nextGroupLineNumber, code);

                    // Check if there are only whitespace characters between line start and group start
                    const leadingText = code.substring(nextGroupLineStart, nextGroupStart);
                    const hasLeadingWhitespace = leadingText.trim() === '' && leadingText.length > 0;

                    if (hasLeadingWhitespace) {
                        // Get the end position of the previous line
                        decorationEnd = this.getLineEndOffset(nextGroupLineNumber - 1, code);
                    } else {
                        // Use original behavior
                        decorationEnd = nextGroupStart - 1;
                    }
                } else {
                    // For the last group, use the end of the last segment in the group
                    // This ensures trailing comments are included in the decoration
                    const lastSegment = group[group.length - 1];
                    if (blockFunctionInfo.isBlockFunction) {
                        // For block functions, use the last segment in the group
                        decorationEnd = lastSegment.end;
                    } else {
                        decorationEnd = code.length;
                    }
                }

                const startLineNumber = this.getLineNumber(decorationStart, code);
                const endLineNumber = this.getLineNumber(decorationEnd, code);
                const startLineOffset = this.getLineStartOffset(startLineNumber, code);

                const decorationsPosition = {
                    lineNumberStart: startLineNumber,
                    lineNumberEnd: endLineNumber,
                    columnStart: decorationStart - startLineOffset + 1,
                    columnEnd: lines[endLineNumber - 1].length + 1,
                };

                // Create the function call
                const call: ParsedFunctionCall = {
                    methodName:
                        firstNonCommentSegment.type === CodeSegmentType.HealFunctionCall
                            ? firstNonCommentSegment.methodName || ''
                            : 'execPlaywright',
                    args: firstNonCommentSegment.args
                        ? firstNonCommentSegment.args
                        : // For playwright segments, we take the source code as argument to clear the step actions
                          // if we change the code in the editor
                          firstNonCommentSegment.type === CodeSegmentType.ExecPlaywrightSegment
                          ? [
                                {
                                    value: code
                                        .substring(firstNonCommentSegment.start, lastNonCommentSegment.end)
                                        .trim(),
                                    type: 'PlaywrightSegment',
                                },
                            ]
                          : [],
                    functionPosition,
                    decorationsPosition,
                };

                // If there's a variableName in the first non-comment segment, add it to the function call
                if (firstNonCommentSegment.variableName) {
                    call.variableName = firstNonCommentSegment.variableName;

                    if (DEBUG_PARSER) {
                        // eslint-disable-next-line no-console
                        console.log(`Adding variableName to function call: ${call.variableName}`);
                    }
                }

                if (DEBUG_PARSER) {
                    this.logFunctionCall(call, `Processing function call: ${call.methodName}`);
                }

                functionCalls.push(call);
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Error parsing function calls:', e);
        }

        return functionCalls;
    }

    /**
     * Get position information (line and column) for a range in the code.
     * @param start - The start offset in the code
     * @param end - The end offset in the code
     * @param code - The original source code
     * @returns An object with line and column information
     */
    private getPositionInfo(start: number, end: number, code: string) {
        const startLine = this.getLineNumber(start, code);
        const endLine = this.getLineNumber(end, code);

        // Calculate column positions
        const startLineStart = this.getLineStartOffset(startLine, code);
        const endLineStart = this.getLineStartOffset(endLine, code);

        return {
            lineNumberStart: startLine,
            lineNumberEnd: endLine,
            columnStart: start - startLineStart + 1,
            columnEnd: end - endLineStart + 1,
        };
    }

    /**
     * Get the line number for a given offset in the code. 1-based.
     * @param offset - The character offset in the code
     * @param code - The original source code
     * @returns The line number (1-based)
     */
    private getLineNumber(offset: number, code: string): number {
        const lines = code.substring(0, offset).split('\n');
        return lines.length;
    }

    /**
     * Get the offset of the start of a line.
     * @param lineNumber - The line number (1-based)
     * @param code - The original source code
     * @returns The character offset of the line start
     */
    private getLineStartOffset(lineNumber: number, code: string): number {
        const lines = code.split('\n');
        let offset = 0;

        for (let i = 0; i < lineNumber - 1; ++i) {
            offset += lines[i].length + 1; // +1 for the newline character
        }

        return offset;
    }

    /**
     * Get the offset of the end of a line (last character, not including newline).
     * @param lineNumber - The line number (1-based)
     * @param code - The original source code
     * @returns The character offset of the line end
     */
    private getLineEndOffset(lineNumber: number, code: string): number {
        const lines = code.split('\n');

        // Handle edge cases
        if (lineNumber < 1 || lineNumber > lines.length) {
            return code.length;
        }

        const lineStartOffset = this.getLineStartOffset(lineNumber, code);
        const lineContent = lines[lineNumber - 1];

        // Return the position of the last character in the line
        return lineStartOffset + lineContent.length;
    }

    /**
     * Validates the syntax of the given TypeScript code.
     * @param code - The TypeScript code to validate
     * @returns An object containing validation results and any errors found
     */
    validateSyntax(code: string): { isValid: boolean; errors: string[] } {
        try {
            const ast = acorn.parse(code, {
                ecmaVersion: 'latest',
                sourceType: 'module',
                allowAwaitOutsideFunction: true,
                locations: true,
                ranges: true,
            });

            const errors: string[] = [];
            this.validateBlockFunctionUsage(ast, code, errors);

            return {
                isValid: errors.length === 0,
                errors,
            };
        } catch (error: any) {
            const errorMessage = error.message || 'Unknown parsing error';
            const line = error.loc?.line || 0;
            const column = error.loc?.column || 0;

            // eslint-disable-next-line no-console
            console.error(`Error parsing code: ${errorMessage} at line ${line}, column ${column}`);

            return {
                isValid: false,
                errors: [`Line ${line}, column ${column}: ${errorMessage}`],
            };
        }
    }

    /**
     * Validates block function usage patterns in the AST.
     * @param ast - The parsed AST
     * @param code - The original source code
     * @param errors - Array to collect validation errors
     */
    private validateBlockFunctionUsage(ast: any, code: string, errors: string[]): void {
        // Helper to check if call is Heal.blocks.blockName
        const isBlockCall = (node: any) =>
            node.callee?.type === 'MemberExpression' &&
            node.callee.object?.type === 'MemberExpression' &&
            node.callee.object.object?.name === 'Heal' &&
            node.callee.object.property?.name === 'blocks';

        // Traverse AST and validate
        const visit = (node: any) => {
            if (node.type === 'CallExpression' && isBlockCall(node)) {
                // Check for direct object/array arguments
                node.arguments.forEach((arg: any) => {
                    if (arg.type === 'ObjectExpression' || arg.type === 'ArrayExpression') {
                        const line = this.getLineNumber(arg.start, code);
                        const argType = arg.type === 'ObjectExpression' ? 'object' : 'array';
                        errors.push(
                            `Line ${line}: Cannot pass ${argType} directly to block function. ` +
                                `Please assign it to a variable first.`,
                        );
                    }
                });
            }

            if (node.type === 'ReturnStatement' && node.argument) {
                // Check for direct return of block function
                const callExpr = node.argument.type === 'AwaitExpression' ? node.argument.argument : node.argument;
                if (callExpr.type === 'CallExpression' && isBlockCall(callExpr)) {
                    const line = this.getLineNumber(node.start, code);
                    errors.push(
                        `Line ${line}: Cannot return block function directly. ` +
                            `Please assign the result to a variable first.`,
                    );
                }
            }

            // Recursively visit children
            Object.values(node).forEach((child) => {
                if (Array.isArray(child)) {
                    child.forEach((item) => item?.type && visit(item));
                } else if (child && typeof child === 'object' && 'type' in child) {
                    visit(child);
                }
            });
        };

        visit(ast);
    }

    /**
     * Logs detailed information about a function call
     * @param call - The function call to log
     * @param context - Additional context about where the call was found
     */
    private logFunctionCall(call: ParsedFunctionCall, context: string) {
        if (!DEBUG_PARSER) return;

        // eslint-disable-next-line no-console
        console.log(`\n=== ${context} ===`);
        // eslint-disable-next-line no-console
        console.log(`Method: ${call.methodName}`);
        // eslint-disable-next-line no-console
        console.log(`Arguments: ${JSON.stringify(call.args)}`);
        if (call.variableName) {
            // eslint-disable-next-line no-console
            console.log(`Variable name: ${call.variableName}`);
        }
        // eslint-disable-next-line no-console
        console.log('Function position:', {
            lineNumberStart: call.functionPosition.lineNumberStart,
            lineNumberEnd: call.functionPosition.lineNumberEnd,
            columnStart: call.functionPosition.columnStart,
            columnEnd: call.functionPosition.columnEnd,
        });
        // eslint-disable-next-line no-console
        console.log('Decorations position:', {
            lineNumberStart: call.decorationsPosition.lineNumberStart,
            lineNumberEnd: call.decorationsPosition.lineNumberEnd,
            columnStart: call.decorationsPosition.columnStart,
            columnEnd: call.decorationsPosition.columnEnd,
        });
    }

    /**
     * Finds all occurrences of the given variable names in the code,
     * including assignments and usage in expressions.
     *
     * @param code - The source code to analyze
     * @param variables - Array of variables to search for
     * @returns Map of variable names to arrays of their position information
     */
    findVariablePositions(code: string, variables: Record<string, string>): Map<string, VariablePosition[]> {
        if (!Object.keys(variables).length) {
            return new Map();
        }

        const variableSet = new Set(Object.keys(variables));
        const results = new Map<string, VariablePosition[]>();

        // Initialize result map
        for (const name of variableSet) {
            results.set(name, []);
        }

        try {
            // Parse the code
            const ast = acorn.parse(code, {
                ecmaVersion: 'latest',
                sourceType: 'module',
                allowAwaitOutsideFunction: true,
                locations: true,
                ranges: true,
            });

            // Visitor function to traverse the AST
            const visitNode = (node: any) => {
                // Handle variable declarations (assignments)
                if (node.type === 'VariableDeclaration') {
                    for (const declarator of node.declarations) {
                        if (declarator.id && declarator.id.type === 'Identifier') {
                            const name = declarator.id.name;

                            if (variableSet.has(name)) {
                                const positions = results.get(name) || [];
                                positions.push({
                                    type: 'assignment',
                                    position: this.getPositionInfo(declarator.start, declarator.end, code),
                                    value: variables[name],
                                });
                                results.set(name, positions);
                            }
                        }
                    }
                }

                // Handle assignment expressions
                else if (node.type === 'AssignmentExpression') {
                    if (node.left.type === 'Identifier' && variableSet.has(node.left.name)) {
                        const name = node.left.name;
                        const positions = results.get(name) || [];
                        positions.push({
                            type: 'assignment',
                            position: this.getPositionInfo(node.start, node.end, code),
                            value: variables[name],
                        });
                        results.set(name, positions);
                    }
                }

                // Handle template literals specifically
                else if (node.type === 'TemplateLiteral' && node.expressions) {
                    for (const expr of node.expressions) {
                        if (expr.type === 'Identifier' && variableSet.has(expr.name)) {
                            const name = expr.name;
                            const positions = results.get(name) || [];
                            positions.push({
                                type: 'usage',
                                position: this.getPositionInfo(expr.start, expr.end, code),
                                value: variables[name],
                            });
                            results.set(name, positions);
                        }
                        // Handle nested expressions within template literals
                        else {
                            visitNode(expr);
                        }
                    }
                }

                // Handle Identifier usage (references)
                else if (node.type === 'Identifier' && variableSet.has(node.name)) {
                    // Skip identifiers that are part of variable declarations or assignments
                    // as they are already handled above
                    const parent = node.parent;
                    if (parent) {
                        if (
                            (parent.type === 'VariableDeclarator' && parent.id === node) ||
                            (parent.type === 'AssignmentExpression' && parent.left === node)
                        ) {
                            return;
                        }
                    }

                    const name = node.name;
                    const positions = results.get(name) || [];
                    positions.push({
                        type: 'usage',
                        position: this.getPositionInfo(node.start, node.end, code),
                        value: variables[name],
                    });
                    results.set(name, positions);
                }

                // Recurse into child nodes
                for (const key in node) {
                    if (node[key] && typeof node[key] === 'object') {
                        // Skip the 'parent' property to avoid circular references
                        if (key === 'parent') continue;

                        if (Array.isArray(node[key])) {
                            for (const child of node[key]) {
                                if (child && typeof child === 'object') {
                                    // Set parent reference for context
                                    child.parent = node;
                                    visitNode(child);
                                }
                            }
                        } else if (node[key].type) {
                            // Set parent reference for context
                            node[key].parent = node;
                            visitNode(node[key]);
                        }
                    }
                }
            };

            // Start traversal at the root node
            visitNode(ast);
        } catch (err) {
            if (DEBUG_PARSER) {
                // eslint-disable-next-line no-console
                console.error('Error finding variable positions:', err);
            }
        }

        return results;
    }

    /**
     * Analyzes highlighted text to determine if it is a variable, starts with a variable, or is a function call.
     *
     * @param code - The source code string to parse
     * @param selectedText - The highlighted text to analyze
     * @returns An object with information about the highlighted text type
     */
    analyzeHighlightedText(
        code: string,
        selectedText: string,
    ): {
        type: 'variable' | 'variable-method' | 'function' | 'unknown';
        functionPart?: string;
    } {
        if (!selectedText || !code || selectedText.trim() === '') {
            return { type: 'unknown' };
        }

        try {
            // Try to parse the selected text directly to see if it's a valid JavaScript expression
            const expressionAst = acorn.parseExpressionAt(selectedText, 0, {
                ecmaVersion: 'latest',
                sourceType: 'module',
                allowAwaitOutsideFunction: true,
                locations: true,
                ranges: true,
            });

            // Determine the type of the expression
            if (expressionAst.type === 'Identifier') {
                // It's a simple variable like 'loc'
                return { type: 'variable' };
            }

            // Helper function to recursively find the base variable name
            const findBaseVariable = (node: any): string | null => {
                if (!node || !('type' in node)) return null;

                if (node.type === 'Identifier') {
                    return node.name;
                }

                if (node.type === 'MemberExpression') {
                    return findBaseVariable(node.object);
                }

                if (node.type === 'CallExpression' && node.callee) {
                    return findBaseVariable(node.callee);
                }

                return null;
            };

            // Handle member expressions and call expressions with member callee
            if (
                expressionAst.type === 'MemberExpression' ||
                (expressionAst.type === 'CallExpression' &&
                    expressionAst.callee &&
                    'type' in expressionAst.callee &&
                    expressionAst.callee.type === 'MemberExpression')
            ) {
                // Get the object from either a direct MemberExpression or from inside a CallExpression
                const memberExpr =
                    expressionAst.type === 'MemberExpression'
                        ? expressionAst
                        : (expressionAst.callee as acorn.MemberExpression);

                // Find the base variable name recursively
                const variableName = findBaseVariable(memberExpr);

                // Extract the function part (everything after the base variable)
                const functionPart = variableName
                    ? selectedText.substring(selectedText.indexOf('.') + 1)
                    : selectedText;

                if (variableName) {
                    return {
                        type: 'variable-method',
                        functionPart,
                    };
                }
            }

            // Handle chained call expressions like page.getByRole('list').nth(1)
            if (expressionAst.type === 'CallExpression') {
                // Find the base variable name recursively
                const variableName = findBaseVariable(expressionAst);

                if (variableName) {
                    // Extract the function part (everything after the base variable)
                    const functionPart = selectedText.substring(selectedText.indexOf('.') + 1);
                    return {
                        type: 'variable-method',
                        functionPart,
                    };
                }

                // Handle direct function calls (no base variable)
                if (
                    expressionAst.callee &&
                    'type' in expressionAst.callee &&
                    expressionAst.callee.type === 'Identifier'
                ) {
                    return {
                        type: 'function',
                        functionPart: selectedText,
                    };
                }
            }

            return { type: 'unknown' };
        } catch (err) {
            if (DEBUG_PARSER) {
                // eslint-disable-next-line no-console
                console.error('Error analyzing highlighted text:', err);
            }
            return { type: 'unknown' };
        }
    }
}
